import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, Share2, Printer, CheckCircle2, MapPin, Truck, Calendar, Receipt, Sparkles, Image as ImageIcon } from 'lucide-react';
import type { JobRecord } from '../lib/activityStore';
interface Props {
  job: JobRecord;
  onClose: () => void;
}
// Backs out a clean breakdown from the stored final price.
// total = (subtotal + serviceFee) * (1 + vatRate)
// serviceFee = subtotal * 0.05
function computeBreakdown(total: number) {
  const vatRate = 0.05;
  const serviceRate = 0.05;
  const preVat = total / (1 + vatRate);
  const vat = total - preVat;
  const subtotal = preVat / (1 + serviceRate);
  const serviceFee = preVat - subtotal;
  return {
    subtotal: Math.round(subtotal),
    serviceFee: Math.round(serviceFee),
    vat: Math.round(vat),
    total: Math.round(total)
  };
}
function formatInvoiceNo(jobId: string) {
  // Stable, readable invoice number derived from the job id
  const hash = jobId.replace(/[^a-z0-9]/gi, '').toUpperCase().slice(-6);
  const d = new Date();
  return `INV-${d.getFullYear()}-${hash}`;
}
export function ReceiptModal({
  job,
  onClose
}: Props) {
  const total = job.price || 0;
  const {
    subtotal,
    serviceFee,
    vat
  } = computeBreakdown(total);
  const issuedAt = job.completedAt || job.startedAt || job.createdAt;
  const invoiceNo = formatInvoiceNo(job.id);
  const customerTrn = 'AE100123456789012';
  const supplierTrn = 'AE100' + (job.supplierName?.length || 7) + '00765432100';
  const handlePrint = () => window.print();
  return <AnimatePresence data-id="element-416">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-id="element-417">
        <motion.div initial={{
        y: 30,
        scale: 0.98
      }} animate={{
        y: 0,
        scale: 1
      }} exit={{
        y: 30,
        scale: 0.98
      }} onClick={e => e.stopPropagation()} className="bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-w-md w-full max-h-[92vh] flex flex-col overflow-hidden" data-id="element-418">
          {/* Header bar */}
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50 shrink-0" data-id="element-419">
            <div className="flex items-center gap-2.5" data-id="element-420">
              <div className="w-9 h-9 rounded-xl bg-gray-900 text-white flex items-center justify-center" data-id="element-421">
                <Receipt className="w-4.5 h-4.5" data-id="element-422" />
              </div>
              <div data-id="element-423">
                <p className="font-bold text-gray-900 text-sm leading-tight" data-id="element-424">
                  Tax Invoice
                </p>
                <p className="text-[10px] text-gray-500 font-medium tracking-wide" data-id="element-425">
                  {invoiceNo}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1" data-id="element-426">
              <button onClick={handlePrint} className="w-9 h-9 rounded-lg hover:bg-white text-gray-500 flex items-center justify-center" title="Print" data-id="element-427">
                <Printer className="w-4 h-4" data-id="element-428" />
              </button>
              <button className="w-9 h-9 rounded-lg hover:bg-white text-gray-500 flex items-center justify-center" title="Share" data-id="element-429">
                <Share2 className="w-4 h-4" data-id="element-430" />
              </button>
              <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white text-gray-500 flex items-center justify-center" aria-label="Close" data-id="element-431">
                <X className="w-4 h-4" data-id="element-432" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto" data-id="element-433">
            {/* Brand block */}
            <div className="px-6 pt-6 pb-5 text-center border-b border-dashed border-gray-200" data-id="element-434">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-100" data-id="element-435">
                <CheckCircle2 className="w-3.5 h-3.5" data-id="element-436" />
                <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-437">
                  Paid · Job completed
                </span>
              </div>
              <h2 className="text-2xl font-display font-black text-gray-900 mb-1" data-id="element-438">
                Pair2 Move
              </h2>
              <p className="text-[11px] text-gray-500 leading-relaxed" data-id="element-439">
                Pair2 Logistics LLC · Dubai, UAE
                <br data-id="element-440" />
                TRN {supplierTrn} · pair2.ae
              </p>
            </div>

            {/* Meta */}
            <div className="px-6 py-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs border-b border-gray-100" data-id="element-441">
              <Field label="Invoice no." value={invoiceNo} icon={<Receipt className="w-3 h-3" data-id="element-443" />} data-id="element-442" />
              <Field label="Issued" value={new Date(issuedAt).toLocaleDateString('en-AE', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })} icon={<Calendar className="w-3 h-3" data-id="element-445" />} data-id="element-444" />
              <Field label="Carrier" value={job.supplierName || '—'} icon={<Truck className="w-3 h-3" data-id="element-447" />} data-id="element-446" />
              <Field label="Job ID" value={job.id.toUpperCase()} icon={<Sparkles className="w-3 h-3" data-id="element-449" />} data-id="element-448" />
            </div>

            {/* Service block */}
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-450">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2" data-id="element-451">
                Service
              </p>
              <div className="bg-gray-50 rounded-2xl p-4 space-y-3" data-id="element-452">
                <div className="flex items-start gap-2.5" data-id="element-453">
                  <Truck className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" data-id="element-454" />
                  <div className="flex-1 min-w-0" data-id="element-455">
                    <p className="font-bold text-gray-900 text-sm leading-tight" data-id="element-456">
                      {job.serviceName}
                    </p>
                    {job.supplierTruck && <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-457">
                        {job.supplierTruck}
                      </p>}
                  </div>
                </div>
                {(job.pickup || job.dropoff) && <div className="flex items-start gap-2.5" data-id="element-458">
                    <MapPin className="w-4 h-4 text-brand-500 shrink-0 mt-0.5" data-id="element-459" />
                    <div className="text-[11px] leading-relaxed" data-id="element-460">
                      <p className="text-gray-500" data-id="element-461">
                        From{' '}
                        <span className="font-semibold text-gray-900" data-id="element-462">
                          {job.pickup}
                        </span>
                      </p>
                      <p className="text-gray-500" data-id="element-463">
                        To{' '}
                        <span className="font-semibold text-gray-900" data-id="element-464">
                          {job.dropoff}
                        </span>
                      </p>
                    </div>
                  </div>}
                {job.prompt && <p className="text-[11px] italic text-gray-600 border-l-2 border-gray-200 pl-2.5 leading-relaxed" data-id="element-465">
                    "{job.prompt}"
                  </p>}
                {job.photos && job.photos.length > 0 && <div className="flex items-center gap-2 pt-1" data-id="element-466">
                    <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" data-id="element-467" />
                    <div className="flex gap-1 flex-1 overflow-x-auto" data-id="element-468">
                      {job.photos.slice(0, 5).map((src, i) => <img key={i} src={src} alt={`Attachment ${i + 1}`} className="w-10 h-10 rounded-md object-cover border border-white shadow-sm shrink-0" data-id="element-469" />)}
                      {job.photos.length > 5 && <div className="w-10 h-10 rounded-md bg-white border border-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0" data-id="element-470">
                          +{job.photos.length - 5}
                        </div>}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Itemized breakdown */}
            <div className="px-6 py-4" data-id="element-471">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-3" data-id="element-472">
                Charges
              </p>
              <div className="space-y-2 text-sm" data-id="element-473">
                <Row label="Carrier fee" desc={job.supplierName || 'Service provider'} value={`AED ${subtotal}`} data-id="element-474" />
                <Row label="Pair2 service fee" desc="5% platform fee" value={`AED ${serviceFee}`} data-id="element-475" />
                <Row label="VAT (5%)" desc="UAE Federal Tax Authority" value={`AED ${vat}`} data-id="element-476" />
              </div>

              <div className="mt-4 pt-4 border-t-2 border-dashed border-gray-200 flex items-end justify-between" data-id="element-477">
                <div data-id="element-478">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500" data-id="element-479">
                    Total paid
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-480">
                    Includes VAT of AED {vat}
                  </p>
                </div>
                <p className="text-3xl font-display font-black text-gray-900 leading-none" data-id="element-481">
                  AED {total}
                </p>
              </div>

              {/* VAT compliance note */}
              <div className="mt-4 rounded-xl bg-blue-50/60 border border-blue-100 p-3 flex gap-2.5" data-id="element-482">
                <Receipt className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" data-id="element-483" />
                <div className="text-[10px] text-blue-900 leading-relaxed" data-id="element-484">
                  <p className="font-bold mb-0.5" data-id="element-485">VAT Tax Invoice</p>
                  <p data-id="element-486">
                    This invoice complies with UAE VAT regulations. Customer
                    TRN: {customerTrn}. Keep for your records.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center" data-id="element-487">
              <p className="text-[10px] text-gray-500 leading-relaxed" data-id="element-488">
                Thank you for moving with Pair2.
                <br data-id="element-489" />
                Questions about this receipt? Email{' '}
                <span className="font-semibold text-gray-700" data-id="element-490">
                  billing@pair2.ae
                </span>
              </p>
            </div>
          </div>

          {/* Action bar */}
          <div className="px-6 py-3 border-t border-gray-100 flex gap-2 shrink-0 bg-white" data-id="element-491">
            <button onClick={onClose} className="flex-1 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-semibold text-sm" data-id="element-492">
              Close
            </button>
            <button onClick={handlePrint} className="flex-1 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5" data-id="element-493">
              <Download className="w-4 h-4" data-id="element-494" />
              Save PDF
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}
function Field({
  label,
  value,
  icon
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
}) {
  return <div data-id="element-495">
      <p className="text-[9px] uppercase tracking-wider font-bold text-gray-400 mb-0.5 flex items-center gap-1" data-id="element-496">
        {icon}
        {label}
      </p>
      <p className="text-xs font-semibold text-gray-900 truncate" data-id="element-497">{value}</p>
    </div>;
}
function Row({
  label,
  desc,
  value
}: {
  label: string;
  desc?: string;
  value: string;
}) {
  return <div className="flex justify-between items-start gap-3" data-id="element-498">
      <div className="min-w-0" data-id="element-499">
        <p className="text-gray-700 font-medium" data-id="element-500">{label}</p>
        {desc && <p className="text-[10px] text-gray-400" data-id="element-501">{desc}</p>}
      </div>
      <p className="font-semibold text-gray-900 shrink-0" data-id="element-502">{value}</p>
    </div>;
}